import { ref, watch, computed, getCurrentInstance } from '@vue/composition-api'
import store from '@/store'
import useNotifications from '@/composables/useNotifications'
import { permissionScope, hasPermission } from '@/composables/usePermission'
import i18n from '@/libs/i18n'
import useCommon from '@/views/organization/useCommon'
import { EventBus } from "@/views/habit/EventBus.js"
import useCommonTodo from '@/views/apps/todo/useCommonTodo'
import awsConnection from '@/views/habit/aws'
import { useLambda } from '@/views/habit/awsLambda'
import useCaseEdit from '@/views/organization/case/edit/useCaseEdit'
import { oid } from '@/helpers/mongoHelpers'
import useMongoDBFunctions from '@/views/habit/useMongoDBFunctions'

export default function useCaseList() {
  const { showSuccessMessage, showErrorMessage } = useNotifications()
  const { handleError, openDocument, downloadZip } = useCommon()
  const { sendEmailCase, uploadFileToS3 } = useCommonTodo()
  const { updateItem, updateItems } = useLambda()
  const { singleUpload } = awsConnection()
  const { casesQueryWithOffset } = useMongoDBFunctions()
  const { client, worker_id } = store.state?.userStore?.userData
  const { default_language } = JSON.parse(localStorage.getItem('clientData') || '{}')
  const refCaseTable = ref(null)
  const cases = ref([])
  const caseStats = ref({})
  const perPage = ref(10)
  const totalCases = ref(0)
  const currentPage = ref(1)
  const searchQuery = ref('')
  const sortBy = ref('createdAt')
  const isSortDirDesc = ref(true)
  const isLoading = ref(true)
  const workerFilter = ref([])
  const activeState = ref('total')
  const activeStage = ref(null)
  const pendingTasksFilter = ref(false)
  const statesData = ref({})
  const stagesData = ref({})
  const workersData = ref({})

  // Global filters from store
  const companyFilter = computed(() => store.state.filtersStore.companyFilter)
  const idFilter = computed(() => store.state.filtersStore.idFilter)
  const processFilter = computed(() => store.state.filtersStore.processFilter)
  const crimeFilter = computed(() => store.state.filtersStore.crimeFilter)
  const controlFilter = computed(() => store.state.filtersStore.controlFilter)
  const processResponsibleFilter = computed(() => store.state.filtersStore.processResponsibleFilter)

  const clearFiltersDisabled = computed(() => !searchQuery.value && activeState.value === 'total' && !activeStage.value && !workerFilter.value?.length && !pendingTasksFilter.value && !companyFilter.value?.length && !idFilter.value?.length && !processFilter.value?.length && !crimeFilter.value?.length && !controlFilter.value?.length && !processResponsibleFilter.value?.length)
  const finishJustification = ref("")
  const evidenceFiles = ref([])

  // Table Handlers
  const tableColumns = computed(() => {
    return [
      {
        key: 'Actions',
        label: i18n.t('message.tableHeader.actions'),
      },
      {
        key: 'code',
        label: i18n.t('message.tableHeader.code'),
        sortable: true,
      },
      {
        key: 'note',
        label: i18n.t('label.Title'),
        sortable: true,
      },
      {
        key: 'companyName',
        label: i18n.t('label.company'),
        sortable: true,
      },
      {
        key: 'createdAt',
        label: i18n.t('label.createdAt'),
        sortable: true,
      },
      {
        key: 'workerName',
        label: i18n.t('label.registration_responsible'),
        sortable: true,
      },
      {
        key: 'stage',
        label: i18n.t('improvement_opp.stage'),
        sortable: true,
      },
      {
        key: 'dueDate',
        label: i18n.t('label.next_stage_due_date'),
        sortable: true,
      },
      {
        key: 'state',
        label: i18n.t('improvement_opp.state'),
        sortable: true,
      },
    ]
  })

  const fetchCases = async () => {
    isLoading.value = true

    try {
      const input = {
        client_id: client.$oid,
        workers: workerFilter.value,
        search: searchQuery.value,
        sortBy: sortBy.value,
        sortOrder: isSortDirDesc.value ? -1 : 1,
        limit: perPage.value,
        offset: (currentPage.value * perPage.value) - perPage.value,
        state: activeState.value,
        stage: activeStage.value,
        onlyPendingTasks: pendingTasksFilter.value,
        workerId: worker_id?.$oid,
        // Capa 2: el scope ('own' vs sin restricción) sale de permission_default/overrides
        // por worker en vez del privilegio crudo — ver permisos-plan.md.
        restrictToOwn: permissionScope('case-management', 'view') === 'own',
        // Global filters
        companies: companyFilter.value,
        businessActivities: idFilter.value,
        processes: processFilter.value,
        crimes: crimeFilter.value,
        controls: controlFilter.value,
        controlResponsibles: processResponsibleFilter.value,
      }
  
      const { casesPaginated, casesStats } = await casesQueryWithOffset(input)

      casesPaginated.forEach(e => {
        // Format next stage due date
        const datePropertyName = e.stage === 'registration' ? 'dueDate' : e.stage === 'analysis' ? 'proposalDueDate' : e.stage === 'approval' ? 'approvalDueDate' : 'verificationDueDate'
        const date = e[datePropertyName] ? new Date(e[datePropertyName]) : undefined
        e.dueDate = date?.toLocaleDateString(`${default_language || 'en'}-US`) || ''

        // Format created at date
        const createdAtDate = e.createdAt ? new Date(e.createdAt) : undefined
        e.createdAt = createdAtDate?.toLocaleDateString(`${default_language || 'en'}-US`) || ''

        // Format company names
        e.companyName = e.companyNames?.join(', ') || ''

        // Set state
        e.stateComputed = computed(() => i18n.t(e.state))

        // Set stage with logic: if stage is 'verification' and there's at least 1 action plan in 'corrective', show 'corrective'
        e.stageComputed = computed(() => {
          if (!e.stage) return ''

          // Apply display logic
          const displayStage = e.stage === 'verification' && e.actionPlanStages?.some(plan => plan.stage === 'corrective')
            ? 'corrective'
            : e.stage

          return i18n.t(`caseStages.${displayStage}`)
        })
      })
  
      cases.value = casesPaginated
      caseStats.value = casesStats.stateCounts.statsForCards
      totalCases.value = casesStats.totalCount
      statesData.value = {
        labels: casesStats.stateCounts.labels,
        values: casesStats.stateCounts.values,
        total: casesStats.totalCount
      }
      stagesData.value = {
        labels: casesStats.stageCounts.labels,
        values: casesStats.stageCounts.values,
        total: casesStats.totalCount
      }
      workersData.value = {
        labels: casesStats.assigneeCounts.labels,
        values: casesStats.assigneeCounts.values,
        workerIds: casesStats.assigneeCounts.workerIds,
      }
    } catch (error) {
      console.log(error)
      handleError({ error, defaultMessage: i18n.t('message.err_improvement_opportunities_list') })
    } finally {
      isLoading.value = false
    }
  }

  const deleteCase = async (caseData) => {
    if (!hasPermission('case-management', 'delete')) {
      showErrorMessage(i18n.t('message.you_are_not_authorized'))
      return
    }

    try {
      const query = { _id: oid(caseData._id) }
      const payload = { deleted: true, lastModifiedBy: worker_id?.$oid ? oid(worker_id.$oid) : null }
      const action = { $set: payload }

      await updateItem({ collection: 'improvement', query, action })

      showSuccessMessage(i18n.t('message.improvement_remove'))
      fetchCases()
      sendEmailCase(caseData, "delete", 'assignee', 'case')
    } catch (error) {
      console.log(error)
      showErrorMessage(i18n.t('message.improvement_remove_error'))
    }
  }

  const finishCase = async (caseData, finishJustification, evidenceFiles) => {
    isLoading.value = true
    cases.value = []
    let evidenceKeys = []

    // Upload evidences to AWS
    if (evidenceFiles?.length) {
      const destinationFolder = `${client.$oid}/evidences`
  
      const uploadFilesPromises = evidenceFiles.map(file => {
        if (!file?.name) return null
        return new Promise((resolve, reject) => {
          uploadFileToS3(file, destinationFolder)
            .then((key) => resolve(key))
            .catch((err) => {
              console.log(err)
              resolve(null)
            })
        })
      }) || []
  
      const fileKeys = await Promise.all(uploadFilesPromises)
      evidenceKeys = fileKeys.filter(key => !!key)
    }

    const query = { _id: oid(caseData._id) }
    const payload = {
      stage: 'end',
      finishData: {
        justification: finishJustification,
        evidenceKeys,
      }
    }
    const action = { $set: payload, $push: { allFileKeys: { $each: evidenceKeys || [] } } }

    try {
      await updateItem({ collection: 'improvement', query, action })

      showSuccessMessage(i18n.t('message.improvement_updated'))

      // Finish all PACs
      if (caseData.actionPlans?.length) {
        const query = { _id: { $in: caseData.actionPlans.map(e => oid(e)) } }
        const payload = { stage: 'end' }
        const action = { $set: payload }
        updateItems({ collection: 'action_plan', query, action })
      }
    } catch (error) {
      console.log(error)
      showErrorMessage(i18n.t('message.improvement_update_error'))
      isLoading.value = false
    } finally {
      fetchCases()
      resetFinishModal()
    }
  }

  const resetFinishModal = () => {
    finishJustification.value = ""
    evidenceFiles.value = []
  }

  const handleAddEvidences = (newDocs) => {
    evidenceFiles.value.push(...newDocs)
  }

  const handleDeleteEvidences = (index) => {
    evidenceFiles.value.splice(index, 1)
  }

  const updateFilter = (data) => {
    workerFilter.value = data.workerFilter
    pendingTasksFilter.value = data.pendingTasksFilter
    
    checkPageAndFetch()
  }

  const filterByStage = (stage) => {
    activeStage.value = activeStage.value === stage ? null : stage
    
    checkPageAndFetch()
  }

  const filterByState = (state, origin) => {
    if (activeState.value === state) {
      if (origin === 'fromChart') state = 'total'
      else return
    }
    activeState.value = state

    checkPageAndFetch()
  }

  const clearFilters = () => {
    const fetchData = !searchQuery.value && !workerFilter.value?.length && !pendingTasksFilter.value && !companyFilter.value?.length && !idFilter.value?.length && !processFilter.value?.length && !crimeFilter.value?.length && !controlFilter.value?.length && !processResponsibleFilter.value?.length

    searchQuery.value = ''
    activeState.value = 'total'
    activeStage.value = null

    // Clear global filters
    store.dispatch('filtersStore/setCompanyFilter', [])
    store.dispatch('filtersStore/setIdFilter', [])
    store.dispatch('filtersStore/setProcessFilter', [])
    store.dispatch('filtersStore/setCrimeFilter', [])
    store.dispatch('filtersStore/setControlFilter', [])
    store.dispatch('filtersStore/setProcessResponsibleFilter', [])

    EventBus.$emit("resetFilters");

    if (!fetchData) return    // This is to avoid duplicate data fetches

    checkPageAndFetch()
  }

  const checkPageAndFetch = () => {
    if (currentPage.value === 1) fetchCases()
    else currentPage.value = 1
  }

  watch([currentPage], () => {
    fetchCases()
  })

  watch([searchQuery, sortBy, isSortDirDesc, perPage], () => {
    checkPageAndFetch()
  })

  watch([companyFilter, idFilter, processFilter, crimeFilter, controlFilter, processResponsibleFilter], () => {
    checkPageAndFetch()
  })

  // Download to PDF
  const { proxy } = getCurrentInstance()
  const { show, caseData } = useCaseEdit()
  const isDownloading = ref(false)

  const downloadPDF = async (caseId) => {
    if (!hasPermission('case-management', 'export')) {
      showErrorMessage(i18n.t('message.you_are_not_authorized'))
      return
    }

    try {
      isDownloading.value = true
      await show(caseId)
      const data = caseData.value

      // Eliminar cualquier contenedor de impresión anterior
      const oldContainer = document.getElementById('print-container')
      if (oldContainer) oldContainer.remove()

      // Crear contenedor oculto
      const container = document.createElement('div')
      container.id = 'print-container'
      container.style.position = 'fixed'
      container.style.top = '-9999px'
      container.style.left = '-9999px'
      document.body.appendChild(container)

      // Crear y montar el componente imprimible
      const FormConstructor = proxy.$root.$options.components.CasePrintableForm
      const vm = new proxy.$root.constructor({
        parent: proxy.$root,
        render: (h) => h(FormConstructor, { props: { data } }),
      }).$mount()

      container.appendChild(vm.$el)

      // Esperar un frame para asegurar render completo
      await new Promise(resolve => setTimeout(resolve, 150))

      // Cambiar posición para impresión
      container.style.top = '0'
      container.style.left = '0'

      // Función de limpieza
      const cleanup = () => {
        vm.$destroy()
        if (container.parentNode) container.parentNode.removeChild(container)
        window.removeEventListener('afterprint', cleanup)
        isDownloading.value = false
      }

      // Escuchar cuando termina la impresión (incluso si se cancela)
      window.addEventListener('afterprint', cleanup, { once: true })

      // Abrir cuadro de impresión
      window.print()

      // Descargar todos los documentos y evidencias del caso
      if (data.allFileKeys?.length) {
        await downloadZip(data.allFileKeys)
      }
    } catch (error) {
      console.error('Error al imprimir:', error)
      isDownloading.value = false
    }
  }

  return {
    fetchCases,
    tableColumns,
    perPage,
    currentPage,
    totalCases,
    searchQuery,
    sortBy,
    isSortDirDesc,
    refCaseTable,
    cases,
    deleteCase,
    isLoading,
    caseStats,
    updateFilter,
    filterByState,
    filterByStage,
    activeState,
    statesData,
    stagesData,
    workersData,
    clearFilters,
    clearFiltersDisabled,
    finishCase,
    finishJustification,
    resetFinishModal,
    evidenceFiles,
    openDocument,
    downloadPDF,
    isDownloading,
    handleAddEvidences,
    handleDeleteEvidences,
  }
}
